import { IServerSideGetRowsRequest } from 'ag-grid-community';
import { ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { getPageIndex, getPageSize } from 'components/Blotters/Quotes/Grid/hooks-serverside/use-translate-request';
import { useServerSideTranslators } from 'components/Blotters/Quotes/Grid/hooks-serverside/use-translators';
import { useTranslateFilters } from 'components/shared/Blotter/Grid/Hooks/useRequestTranslator';
import { format, parseISO } from 'date-fns';
import { TradesOrProductsBlotterRequest } from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { useCallback } from 'react';
import { ActivityFilter, IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

export type ExternalFilter = Pick<
  TradesOrProductsBlotterRequest,
  'startDate' | 'endDate' | 'currency' | 'platforms' | 'product' | 'status' | 'fullTextSearch' | 'activityScope'
>;

export type MemoizedFilters = { filters?: any };

type ActivityScope = ServerSideBlotterSearchRequest['visibilityContext']['activityScope'];

function mapActivityFilter(activity: ActivityFilter): ActivityScope {
  switch (activity) {
    case 'all':
      return 'Everything';
    case 'myTeam':
      return 'MyTeams';
    case 'mySelf':
      return 'OnlyMe';
    default:
      return undefined;
  }
}

export function toDateOnly(isoDate: string | null) {
  if (isoDate === null) {
    return null;
  }
  const date = parseISO(isoDate);
  return format(date, 'yyyy-MM-dd');
}

export function useTranslateTradesRequest<TData>(columns: readonly IBlotterColumnDefinition<TData>[]) {
  const translators = useServerSideTranslators();
  const translateFilters = useTranslateFilters(translators, columns);

  return useCallback(
    (
      gridParams: IServerSideGetRowsRequest,
      request: ExternalFilter & MemoizedFilters,
    ): ServerSideBlotterSearchRequest => {
      const orders: ServerSideBlotterSearchRequest['pageContext']['orders'] = gridParams.sortModel.map(
        ({ colId: field, sort: direction }) => ({ field, direction }),
      );
      const gridFilters = Object.values(translateFilters(gridParams)).filter(x => x !== null);
      const { currency, startDate, endDate, platforms, product, status } = request;
      const externalFilter = { currency, startDate, endDate, platforms, product, status };

      const pageSize = getPageSize(gridParams);
      const pageIndex = getPageIndex(gridParams);

      return {
        externalFilter,
        filters: gridFilters,
        fullTextContext: (request.fullTextSearch?.length ?? 0) > 0 ? { query: request.fullTextSearch! } : undefined,
        pageContext: { orders: orders.length > 0 ? orders : [], page: pageIndex ?? 0, size: pageSize ?? 25 },
        visibilityContext: {
          activityScope: mapActivityFilter(request.activityScope),
        },
      };
    },
    [translateFilters],
  );
}
